import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  computeDuplicatePairsCore,
  DuplicateMatchPatientPayload,
  DuplicatePairRow,
  normalizeDuplicatePairRow,
  toDuplicateMatchPayload
} from '../duplicate-match/patient-duplicate-match-core';
import { PatientService } from './patient.service';

export type {
  DuplicateMatchDetail,
  DuplicatePairKind,
  DuplicateMatchPatientPayload,
  DuplicatePairRow
} from '../duplicate-match/patient-duplicate-match-core';

@Injectable({
  providedIn: 'root'
})
export class PatientDuplicateMatchService {
  /** Latest computed pairs (single computation per patients$ update). */
  readonly duplicatePairs$ = new BehaviorSubject<DuplicatePairRow[]>([]);

  /** True while a worker request is in flight (or brief sync compute). */
  readonly duplicatePairsLoading$ = new BehaviorSubject<boolean>(false);

  private worker: Worker | null = null;
  private requestSeq = 0;
  private patientsSub: Subscription;

  constructor(
    private patientService: PatientService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.patientsSub = this.patientService.patients$
      .pipe(debounceTime(80))
      .subscribe(patients => this.scheduleCompute(patients));
  }

  /**
   * Synchronous compute (same rules as worker). Use for tests or when Worker is unavailable.
   */
  computeDuplicatePairs(patients: any[] | null | undefined): DuplicatePairRow[] {
    const slim = (patients ?? []).map(toDuplicateMatchPayload);
    return computeDuplicatePairsCore(slim).map(normalizeDuplicatePairRow);
  }

  private scheduleCompute(patients: any[] | null | undefined): void {
    const slim: DuplicateMatchPatientPayload[] = (patients ?? [])
      .filter(p => p?.active === true)
      .map(toDuplicateMatchPayload);

    // A worker runs its queue in order, so a scan of an older patient list would finish before this
    // one starts. Stop it: during the paged load that is one wasted full scan per page.
    if (this.duplicatePairsLoading$.value && this.worker) {
      this.worker.terminate();
      this.worker = null;
    }

    const requestId = ++this.requestSeq;
    this.duplicatePairsLoading$.next(true);

    const canUseWorker =
      isPlatformBrowser(this.platformId) && typeof Worker !== 'undefined';

    if (canUseWorker) {
      try {
        const w = this.getWorker();
        w.onmessage = (ev: MessageEvent<{ requestId: number; rows: DuplicatePairRow[] }>) => {
          const d = ev.data;
          if (d?.requestId !== this.requestSeq) {
            return;
          }
          const rows = (d.rows ?? []).map(normalizeDuplicatePairRow);
          this.duplicatePairs$.next(rows);
          this.duplicatePairsLoading$.next(false);
        };
        w.onerror = () => {
          this.finishSync(requestId, slim);
        };
        w.postMessage({ requestId, patients: slim });
        return;
      } catch {
        /* fall through */
      }
    }

    this.finishSync(requestId, slim);
  }

  private finishSync(requestId: number, slim: DuplicateMatchPatientPayload[]): void {
    const rows = computeDuplicatePairsCore(slim).map(normalizeDuplicatePairRow);
    if (requestId !== this.requestSeq) {
      return;
    }
    this.duplicatePairs$.next(rows);
    this.duplicatePairsLoading$.next(false);
  }

  private getWorker(): Worker {
    if (!this.worker) {
      this.worker = new Worker(
        new URL('../duplicate-match/patient-duplicate-match.worker', import.meta.url),
        { type: 'module' }
      );
    }
    return this.worker;
  }
}
